// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

export function makeFmt(sig) {
  const digits = Number(sig);
  const usePrecision = Number.isFinite(digits) && digits > 0;
  return (n) => {
    if (!Number.isFinite(n)) return 0;
    if (usePrecision && n !== 0) return Number(n.toPrecision(digits));
    return Math.round(n * 100) / 100;
  };
}
const LAYER_ORDER = ["background", "main", "overlay"];
const RECT_KINDS = new Set([
  "rect",
  "stage",
  "seating",
  "parking",
  "toilet",
  "fence",
  "entrance",
]);
const CIRCLE_KINDS = new Set([
  "circle",
  "tree",
  "trash",
  "fountain_drink",
  "lighting",
  "info_board",
  "fountain_water",
]);

const escapeXml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const escapeAttr = (value) => escapeXml(value).replace(/"/g, "&quot;");

// Rotate (x, y) by `deg` degrees around (cx, cy).
const rotatePoint = (x, y, cx, cy, deg) => {
  if (!deg) return { x, y };
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = x - cx;
  const dy = y - cy;
  return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos };
};

export function buildOptimizedSVG(elements, scenario, options = {}) {
  const {
    layerVisibility = { background: true, main: true, overlay: true },
    backgroundImage = null,
    bakeBackground = true,
    precision,
    minify = false,
    stripEditorData = false,
    viewboxOnly = false,
    removeImages = false,
    // Optional (lng, lat) -> { x, y } projector for OSM-coordinate polygons.
    project = null,
  } = options;

  const fmt = makeFmt(precision);
  const nl = minify ? "" : "\n";
  const pad = (depth) => (minify ? "" : "  ".repeat(depth));

  const num = (n) => fmt(Number(n) || 0);

  // Resolve a polygon vertex (SVG x/y, or OSM lng/lat via `project`).
  const resolvePoint = (p) => {
    if (p == null) return { x: 0, y: 0 };
    if (p.x != null && p.y != null) return { x: p.x, y: p.y };
    if (p.lng != null && p.lat != null) {
      if (typeof project === "function") return project(p.lng, p.lat);
      return { x: p.lng, y: p.lat };
    }
    return { x: 0, y: 0 };
  };

  // Editor-data attributes (id + semantic data-*). Dropped when stripEditorData.
  const editorAttrs = (el) => {
    if (stripEditorData) return "";
    let a = "";
    // The "SVG Code" (space_code) doubles as the element's SVG id when set.
    const svgId = el.space_code || el.id;
    if (svgId) a += ` id="${escapeAttr(svgId)}"`;
    if (el.polygonId) a += ` data-polygon-id="${escapeAttr(el.polygonId)}"`;
    if (el.coordinateSystem)
      a += ` data-coordinate-system="${escapeAttr(el.coordinateSystem)}"`;
    return a;
  };

  const paintAttrs = (el, fallbackFill, fallbackStroke) =>
    `fill="${escapeAttr(el.fill || fallbackFill || "none")}" ` +
    `stroke="${escapeAttr(el.stroke || fallbackStroke || "none")}" ` +
    `stroke-width="${num(el.strokeWidth ?? 1)}"`;

  // Absolute two-arc full-circle path.
  const circleArcPath = (cx, cy, r) =>
    `M ${num(cx - r)} ${num(cy)} ` +
    `A ${num(r)} ${num(r)} 0 1 0 ${num(cx + r)} ${num(cy)} ` +
    `A ${num(r)} ${num(r)} 0 1 0 ${num(cx - r)} ${num(cy)} Z`;

  const rectNode = (el) => {
    const x = Number(el.x) || 0;
    const y = Number(el.y) || 0;
    const w = Number(el.width) || 0;
    const h = Number(el.height) || 0;
    if (w <= 0 || h <= 0) return [];
    const cx = x + w / 2;
    const cy = y + h / 2;
    const rot = el.rotation || 0;
    const corners = [
      [x, y],
      [x + w, y],
      [x + w, y + h],
      [x, y + h],
    ].map(([px, py]) => rotatePoint(px, py, cx, cy, rot));
    const d = `M ${corners
      .map((p) => `${num(p.x)} ${num(p.y)}`)
      .join(" L ")} Z`;
    return [`<path d="${d}" ${paintAttrs(el)}${editorAttrs(el)}/>`];
  };

  const circleNode = (el) => {
    const cx = Number(el.cx) || 0;
    const cy = Number(el.cy) || 0;
    const r = Number(el.r) || 0;
    if (r <= 0) return [];
    // Rotation has no visible effect on a circle — emit it as-is.
    const d = circleArcPath(cx, cy, r);
    return [`<path d="${d}" ${paintAttrs(el)}${editorAttrs(el)}/>`];
  };

  const polygonNode = (el) => {
    if (!Array.isArray(el.points) || el.points.length < 3) return [];
    const pts = el.points.map(resolvePoint);
    const xs = pts.map((p) => p.x);
    const ys = pts.map((p) => p.y);
    const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
    const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
    const rot = el.rotation || 0;
    const rotated = pts.map((p) => rotatePoint(p.x, p.y, cx, cy, rot));
    const d = `M ${rotated
      .map((p) => `${num(p.x)} ${num(p.y)}`)
      .join(" L ")} Z`;
    return [
      `<path d="${d}" ${paintAttrs(el, "#90caf9", "#1565c0")}${editorAttrs(el)}/>`,
    ];
  };

  const textNode = (el) => {
    const x = Number(el.x) || 0;
    const y = Number(el.y) || 0;
    const rot = el.rotation || 0;
    // Text stays <text>; a baked transform is the only way to convey rotation
    // without outlining glyphs, so we keep just a rotate() when needed.
    const transform = rot
      ? ` transform="rotate(${num(rot)} ${num(x)} ${num(y)})"`
      : "";
    const fontFamily = el.fontFamily
      ? ` font-family="${escapeAttr(el.fontFamily)}"`
      : "";
    return [
      `<text x="${num(x)}" y="${num(y)}" font-size="${num(el.fontSize ?? 14)}" ` +
        `fill="${escapeAttr(el.fill || "#333")}"${fontFamily}${transform}` +
        `${editorAttrs(el)}>${escapeXml(el.text)}</text>`,
    ];
  };

  const pinNode = (el) => {
    const x = Number(el.x) || 0;
    const y = Number(el.y) || 0;
    const rot = el.rotation || 0;
    const P = (px, py) => {
      const p = rotatePoint(px, py, x, y, rot);
      return `${num(p.x)} ${num(p.y)}`;
    };
    const d =
      `M ${P(x + 10, y)} ` +
      `C ${P(x + 15, y)} ${P(x + 20, y + 5)} ${P(x + 20, y + 10)} ` +
      `C ${P(x + 20, y + 15)} ${P(x + 15, y + 20)} ${P(x + 10, y + 20)} ` +
      `C ${P(x + 5, y + 20)} ${P(x, y + 15)} ${P(x, y + 10)} ` +
      `C ${P(x, y + 5)} ${P(x + 5, y)} ${P(x + 10, y)} Z ` +
      `M ${P(x + 10, y + 20)} L ${P(x + 10, y + 30)} Z`;
    const body = `<path d="${d}" ${paintAttrs(el, "#f44336", "#c62828")}${editorAttrs(el)}/>`;
    const dot = rotatePoint(x + 10, y + 10, x, y, rot);
    const inner = `<path d="${circleArcPath(dot.x, dot.y, 4)}" fill="#fff"/>`;
    return [body, inner];
  };

  const elementToNodes = (el) => {
    if (!el || el.visible === false) return [];
    if (el.type === "polygon") return polygonNode(el);
    if (el.type === "text") return textNode(el);
    if (el.type === "pin") return pinNode(el);
    if (RECT_KINDS.has(el.type)) return rectNode(el);
    if (CIRCLE_KINDS.has(el.type)) return circleNode(el);
    return [];
  };

  // Baked background <image> (raster only). The fit math mirrors the live
  // renderer's "contain" placement inside the viewBox.
  const backgroundImageNode = () => {
    if (!bakeBackground || removeImages || !backgroundImage) return null;
    if (backgroundImage.type !== "image" || !backgroundImage.data) return null;
    const vb = scenario.viewBox || {};
    const vbX = Number(vb.x) || 0;
    const vbY = Number(vb.y) || 0;
    const vbW = Number(vb.width) || 0;
    const vbH = Number(vb.height) || 0;
    if (vbW <= 0 || vbH <= 0) return null;
    const ow = backgroundImage.originalWidth || vbW;
    const oh = backgroundImage.originalHeight || vbH;
    const imageAspect = ow / oh;
    const vbAspect = vbW / vbH;
    let fitW;
    let fitH;
    let ox;
    let oy;
    if (imageAspect > vbAspect) {
      fitW = vbW;
      fitH = vbW / imageAspect;
      ox = vbX;
      oy = vbY + (vbH - fitH) / 2;
    } else {
      fitH = vbH;
      fitW = vbH * imageAspect;
      ox = vbX + (vbW - fitW) / 2;
      oy = vbY;
    }
    return (
      `<image x="${num(ox)}" y="${num(oy)}" width="${num(fitW)}" ` +
      `height="${num(fitH)}" href="${escapeAttr(backgroundImage.data)}" ` +
      `preserveAspectRatio="xMidYMid meet"/>`
    );
  };

  const list = Array.isArray(elements) ? elements : [];

  const layerBlocks = [];
  for (const layerId of LAYER_ORDER) {
    if (!layerVisibility[layerId]) continue;
    const lines = [];
    if (layerId === "background") {
      const img = backgroundImageNode();
      if (img) lines.push(img);
    }
    list
      .filter((el) => el.layer === layerId)
      .forEach((el) => lines.push(...elementToNodes(el)));
    if (lines.length === 0) continue;
    const inner = lines.map((l) => pad(2) + l).join(nl);
    layerBlocks.push(
      `${pad(1)}<g data-layer="${layerId}">${nl}${inner}${nl}${pad(1)}</g>`,
    );
  }

  const vb = scenario.viewBox || {};
  const vbX = num(vb.x);
  const vbY = num(vb.y);
  const vbW = num(vb.width);
  const vbH = num(vb.height);

  let rootAttrs = 'xmlns="http://www.w3.org/2000/svg"';
  rootAttrs += ` viewBox="${vbX} ${vbY} ${vbW} ${vbH}"`;
  if (!viewboxOnly) rootAttrs += ` width="${vbW}" height="${vbH}"`;
  if (!stripEditorData) {
    if (scenario.name)
      rootAttrs += ` data-scenario-name="${escapeAttr(scenario.name)}"`;
    if (scenario.description)
      rootAttrs += ` data-scenario-description="${escapeAttr(scenario.description)}"`;
    if (scenario.coordinateSystem)
      rootAttrs += ` data-coordinate-system="${escapeAttr(scenario.coordinateSystem)}"`;
  }

  const body = layerBlocks.join(nl);
  return `<svg ${rootAttrs}>${nl}${body}${body ? nl : ""}</svg>`;
}
